/**
 * The e-stop card's steps (twin slice 1, plan W5 step 6; Decision 4).
 *
 * FOH may release the red button after checks. A release can start the
 * robot moving at once -- task 2585 replayed a job that had been cancelled
 * while it was held, and with no job at all it may drive back to its
 * charger (idle behaviour, which is not a task and so cannot be cancelled).
 * So the steps are DYNAMIC: go, cancel its job, wait until the job reads
 * ended in live data, check its path, and only then twist.
 *
 *   - No job: the cancel and wait steps are left out.
 *   - Job ended: "Job cancelled" shows, and cancel and wait read as done.
 *   - Cancel pressed and the job still active 30 s later: the card says
 *     the cancel has not taken effect, and the check and twist steps are
 *     WITHHELD. The robot must not be released into a job that is still
 *     live.
 *   - The robot can't be read now (A7; the selector holds its card up):
 *     the card is the one it had when it stopped being readable, read as
 *     of then -- nobody can read its job since, so it neither escalates
 *     nor changes -- and its floor is where it was last seen. Its steps
 *     stay (A11), but the wait step never promises the card will say
 *     "Job cancelled": a frozen card cannot. It points to Tasks, which
 *     reads the job from the task list, not from the robot (N3).
 *
 * IN OR AT A CAR. When `heldLift()` returns a car (inside it or boarding
 * it), or the robot's own report says it is inside one (`EXIT_BLOCKED:
 * <car>`; `select.ts::estopCar`), the card says not to cancel and not to
 * twist, and to call Nexsoss.
 * This is deliberately stricter than the drawer, which still offers Cancel
 * for a plain active task in a car (`TaskControlActions.tsx`); no parity
 * is claimed.
 *
 * OPS read the same steps under "On site:", closing with the reminder that
 * a release happens only under the written e-stop procedure -- until that
 * procedure exists (`ESTOP_PROCEDURE_WRITTEN`).
 *
 * COPY STATUS: pending Akash's copy pass 2 (plan gate B).
 */
import type { Audience } from '../../auth/roles';
import { levelWords } from './words';
import {
  OUT_OF_LIFT_THEN_QR,
  SCANNED_IT_BUTTON,
  STILL_LOST_CALL,
  type OutOfLiftStepId,
  type QrScanStepId,
} from './robot-copy';

/** How long a seen Cancel may go unanswered by live data before the card
 *  stops offering the release. */
export const CANCEL_TAKES_EFFECT_MS = 30_000;

/** Flips when Akash's written e-stop procedure exists (gate F). Until
 *  then ops are reminded that a release follows it. */
export const ESTOP_PROCEDURE_WRITTEN = false;

/**
 * The robot's job, as the card needs it.
 *
 * `cancelRequestedAtMs` is when THIS PAGE saw a Cancel for the task
 * accepted, on the device clock; null when it has seen none (the person
 * may have cancelled elsewhere, or not yet). The 30 s clock runs only from
 * a cancel this page saw: with none seen, nothing says a cancel failed.
 */
export type EstopJob =
  | { state: 'none' }
  | { state: 'active'; taskId: string; cancelRequestedAtMs: number | null }
  | { state: 'ended'; taskId: string };

export interface EstopInput {
  robot: string;
  /** The robot's map name (`L9`), when its position is trusted. */
  level: string | null;
  job: EstopJob;
  /** `heldLift()`'s car (the one it is in or boarding), or the car its
   *  own report says it is inside. Null when none. */
  heldLift: string | null;
  /** When the card is read. For a robot that can't be read now (A7), when
   *  it stopped being readable: its job is not read since, so the card
   *  neither escalates nor changes. */
  nowMs: number;
  /** The robot can't be read now (A7): `level` is where it was last seen,
   *  not where it is known to be. */
  lastSeen?: boolean;
  audience: Audience;
  /** During a fire every walk step is withdrawn. */
  fireActive?: boolean;
  /** The fleet adapter handed this car back to guests after the e-stop
   *  held it (0.7.19); `relocalize` when the robot was in the car or its
   *  doorway then, so it must be put back on the map. Null otherwise. */
  handedBack?: { lift: string; relocalize: boolean } | null;
}

export type EstopStepId =
  | 'go'
  | 'cancel'
  | 'wait'
  | 'check'
  | 'twist'
  | OutOfLiftStepId
  | QrScanStepId
  | 'carry_on';

export interface EstopStep {
  id: EstopStepId;
  text: string;
  /** Already true in live data (the job reads ended). */
  done: boolean;
}

export type EstopCardKind = 'steps' | 'cancel_stuck' | 'in_car' | 'fire' | 'handed_back';

export interface EstopCard {
  kind: EstopCardKind;
  /** "On site:" for ops; null for a site audience. */
  heading: string | null;
  steps: EstopStep[];
  /** "Job cancelled", once the job reads ended. */
  jobLine: string | null;
  /** The card's one statement when it is not a plain list of steps. */
  notice: string | null;
  /** Ops only, while there is no written procedure. */
  closing: string | null;
}

export const JOB_CANCELLED = 'Job cancelled';
export const CANCEL_STUCK =
  "Cancel hasn't taken effect. Don't twist the button; call Nexsoss.";
export const RELEASE_ONLY_UNDER_PROCEDURE =
  'Release only under the written e-stop procedure.';
export const FIRE_WITHDRAWN = "Follow the hotel's fire procedure.";

export function inCarNotice(lift: string): string {
  return `It is in or at ${lift}. Don't cancel its job and don't twist the button. Call Nexsoss.`;
}

/** The car came back to guests (fleet adapter 0.7.19, decisions E1-E8):
 *  the job is kept and carries on by itself once the button is twisted --
 *  after it is back on the map, when it was in the car. */
export function handedBackNotice(lift: string): string {
  // "Is being": while the car is still finishing a trip (riding, or a call
  // in progress) the hand-back takes effect only when it stops at a floor.
  return `${lift} is being handed back to guests.`;
}
/** Out of the lift, then back on the map: the robot panel's own steps
 *  (`OUT_OF_LIFT_THEN_QR`), one card step each. Released and in manual
 *  mode before it is pushed (D20, D44), pushed out onto the landing
 *  (D33), then the lost-robot card's QR steps from the QR code point
 *  (D35). The card used to open "If it is in the lift, push it out at the
 *  next floor" and only then "Twist the red button": a robot cannot be
 *  pushed with its button pressed and before manual mode (review BC-F6,
 *  2026-10-31). Ops read these steps out to the person at the lift. */
const outOfLiftSteps = (): EstopStep[] => OUT_OF_LIFT_THEN_QR.map((s) => ({ ...s, done: false }));
/** After the scan, the robot panel's press says it is back on the map
 *  (2026-10-12): named as the button reads, for both audiences, since
 *  these are the steps of the person on site. The press only makes it
 *  sooner: back on the map, released and standing still for 10 s, the
 *  job carries on by itself (fleet adapter 0.7.19), so nobody at the
 *  robot may read the press as what starts it (review BC-F4). */
export const CARRY_ON_AFTER_RELOCALIZE =
  `Then press ${SCANNED_IT_BUTTON} in its robot panel: it carries on its job straight away and ` +
  'may call the lift. Without the press it may carry on by itself about 10 seconds after it is ' +
  `back on the map. ${STILL_LOST_CALL}`;
export const CHECK_THEN_CARRY_ON =
  'Check nothing and nobody is in its way. When you twist the button it will call the lift again and carry on its job.';

function goStep(level: string | null, lastSeen: boolean): string {
  const words = levelWords(level);
  if (!words) return "Find it: its floor isn't known.";
  return lastSeen ? `Go to ${words}, where it was last seen.` : `Go to ${words}.`;
}

const CANCEL_STEP =
  'Cancel its job: open its robot panel, then Current task, then Cancel Task.';
const WAIT_STEP = `Wait until this card says "${JOB_CANCELLED}".`;
/** The wait step on a card held while the robot can't be read (N3). */
export const WAIT_STEP_HELD = 'Wait until Tasks shows its job cancelled.';
const CHECK_STEP =
  'Check nothing and nobody is in its way. It may drive off as soon as you twist, for example back to its charger, even with no job.';
// "Red estop button" (D32).
const TWIST_STEP = 'Twist the red estop button to release it.';

export function estopCard(input: EstopInput): EstopCard {
  const ops = input.audience === 'ops';
  const base = {
    heading: ops ? 'On site:' : null,
    closing: ops && !ESTOP_PROCEDURE_WRITTEN ? RELEASE_ONLY_UNDER_PROCEDURE : null,
  };

  if (input.fireActive) {
    return { ...base, kind: 'fire', steps: [], jobLine: null, notice: FIRE_WITHDRAWN };
  }
  if (input.handedBack) {
    const { lift, relocalize } = input.handedBack;
    const steps: EstopStep[] = relocalize
      ? [...outOfLiftSteps(), { id: 'carry_on', text: CARRY_ON_AFTER_RELOCALIZE, done: false }]
      : [
          { id: 'check', text: CHECK_THEN_CARRY_ON, done: false },
          { id: 'twist', text: TWIST_STEP, done: false },
        ];
    return { ...base, kind: 'handed_back', steps, jobLine: null, notice: handedBackNotice(lift) };
  }
  if (input.heldLift) {
    return {
      ...base,
      kind: 'in_car',
      steps: [],
      jobLine: null,
      notice: inCarNotice(input.heldLift),
    };
  }

  const steps: EstopStep[] = [
    { id: 'go', text: goStep(input.level, input.lastSeen === true), done: false },
  ];
  const job = input.job;
  if (job.state !== 'none') {
    const ended = job.state === 'ended';
    steps.push({ id: 'cancel', text: CANCEL_STEP, done: ended });
    steps.push({ id: 'wait', text: input.lastSeen === true ? WAIT_STEP_HELD : WAIT_STEP, done: ended });
  }

  const stuck =
    job.state === 'active' &&
    job.cancelRequestedAtMs !== null &&
    input.nowMs - job.cancelRequestedAtMs >= CANCEL_TAKES_EFFECT_MS;
  if (stuck) {
    return { ...base, kind: 'cancel_stuck', steps, jobLine: null, notice: CANCEL_STUCK };
  }

  steps.push({ id: 'check', text: CHECK_STEP, done: false });
  steps.push({ id: 'twist', text: TWIST_STEP, done: false });
  return {
    ...base,
    kind: 'steps',
    steps,
    jobLine: job.state === 'ended' ? JOB_CANCELLED : null,
    notice: null,
  };
}
